import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { Feather, MaterialCommunityIcons } from '@expo/vector-icons';

import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import PopUp from '../popUp/PopupView';
import VerifyModal from '../../screens/tenants/tenantOnboarding/screens/modals/VerifyModal';
import Text from '../text';
import { COLORS, TYPOGRAPHY, SPACING, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

// ─── Sub-components ─────────────────────────────────────────────────────────

const Badge = ({ completed }: { completed: boolean }) => (
  <View style={[s.badge, completed ? s.badgeDone : s.badgePending]}>
    <Feather
      name={completed ? 'check-circle' : 'alert-circle'}
      size={11}
      color={completed ? COLORS.success : COLORS.warning}
      style={{ marginRight: 4 }}
    />
    <Text
      fontSize={TYPOGRAPHY.fontSize.xs}
      fontWeight={TYPOGRAPHY.fontWeight.bold}
      color={completed ? COLORS.success : COLORS.warning}
    >
      {completed ? 'Completed' : 'Action Required'}
    </Text>
  </View>
);

const ProgressBar = ({ progress, completedCount }: { progress: number; completedCount: number }) => (
  <View style={s.progressWrapper}>
    <View style={s.progressLabelRow}>
      <Text fontSize={TYPOGRAPHY.fontSize.sm} color={COLORS.textSecondary} fontWeight={TYPOGRAPHY.fontWeight.medium}>
        Landlord readiness
      </Text>
      <Text fontSize={TYPOGRAPHY.fontSize.sm} color={COLORS.primary} fontWeight={TYPOGRAPHY.fontWeight.semiBold}>
        {completedCount}/2 completed
      </Text>
    </View>
    <View style={s.progressTrack}>
      <View style={[s.progressFill, { width: `${progress * 100}%` as any }]} />
    </View>
  </View>
);

interface VerifCardProps {
  icon: any;
  title: string;
  description: string;
  verified: boolean;
  disabled: boolean;
  btnLabel: string;
  onPress: () => void;
}

const VerifCard = ({ icon, title, description, verified, disabled, btnLabel, onPress }: VerifCardProps) => (
  <View style={[s.verifCard, verified && s.verifCardDone]}>
    <View style={s.verifCardTop}>
      <View style={[s.iconWrap, verified && s.iconWrapDone]}>
        <Image source={icon} style={s.cardIcon} resizeMode="contain" />
      </View>
      <Badge completed={verified} />
    </View>

    <Text
      fontSize={TYPOGRAPHY.fontSize.md}
      fontWeight={TYPOGRAPHY.fontWeight.bold}
      color={COLORS.textPrimary}
      marginTop={SPACING.sm}
      marginBottom={SPACING.xs}
    >
      {title}
    </Text>
    <Text
      fontSize={TYPOGRAPHY.fontSize.sm}
      color={COLORS.textSecondary}
      lineHeight={20}
      style={{ flex: 1 }}
    >
      {description}
    </Text>

    <TouchableOpacity
      disabled={disabled}
      style={[s.cardBtn, verified ? s.cardBtnDone : disabled ? s.cardBtnLocked : s.cardBtnActive]}
      onPress={onPress}
      activeOpacity={0.82}
    >

      <Text
        fontSize={TYPOGRAPHY.fontSize.base}
        fontWeight={TYPOGRAPHY.fontWeight.semiBold}
        color={verified ? COLORS.success : disabled ? COLORS.textMuted : COLORS.textWhite}
      >
        {btnLabel}
      </Text>
    </TouchableOpacity>
  </View>
);

// ─── Main Component ──────────────────────────────────────────────────────────

interface Props {
  isHome?: boolean;
  userProfile?: any;
  onProfileRefresh?: ((user: any) => void) | null;
  containerStyle?: object;
}

const VerificationSection = ({
  isHome = false,
  userProfile = null,
  onProfileRefresh = null,
  containerStyle = {},
}: Props) => {
  const navigation = useNavigation<any>();
  const focused = useIsFocused();
  const { width } = useWindowDimensions();
  const isMobile = width <= 768;
  const reduxUser = useSelector((state: any) => state?.Login?.userDetails);

  const [user, setUser] = useState<any>(userProfile || reduxUser || {});
  const [fetching, setFetching] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  useEffect(() => {
    if (userProfile) setUser(userProfile);
  }, [userProfile]);

  useEffect(() => {
    if (focused) fetchUserProfile();
  }, [focused]);

  const fetchUserProfile = () => {
    setFetching(true);
    ajax
      .get(Urls.PROFILE)
      .then(({ data }: any) => {
        setFetching(false);
        if (data?.status && data?.data?.userDetails) {
          const fetched = data.data.userDetails;
          setUser(fetched);
          onProfileRefresh?.(fetched);
        }
      })
      .catch((err: any) => {
        setFetching(false);
        console.log('VerificationSection: error fetching profile', err);
      });
  };

  const idVerified = user?.id_verification_status === 'completed';
  const kycVerified = user?.kyc_verification_status === 'completed';
  const paymentDone = !!user?.landlord_verification_payment_done;
  const completedCount = (idVerified ? 1 : 0) + (kycVerified ? 1 : 0);
  const progress = completedCount / 2;

  const handleVerifyPress = () => {
    if (paymentDone) return;
    if (isHome) {
      navigation.navigate('MyAccount', { scrollToVerification: true });
    } else {
      setOpenModal(true);
    }
  };

  const handleIdPress = () => {
    if (!paymentDone || idVerified) return;
    const uid = user?.id || reduxUser?.id;
    if (uid) window.open(`https://dev.idverification.hubita.co.uk/?uid=hub_${uid}`, '_blank');
  };

  return (
    <View nativeID="verification-section" style={[s.container, containerStyle]}>
      {/* Loading overlay */}
      {fetching && (
        <View style={s.loadingOverlay}>
          <ActivityIndicator size="small" color={COLORS.primary} />
        </View>
      )}

      {/* ── Header row ── */}
      <View style={[s.header, isMobile && s.headerMobile]}>
        <View style={s.headerTextWrap}>
          {/* pill tag */}
          <View style={s.tag}>
            <MaterialCommunityIcons name="shield-check-outline" size={13} color={COLORS.primary} style={{ marginRight: 5 }} />
            <Text fontSize={TYPOGRAPHY.fontSize.xs} fontWeight={TYPOGRAPHY.fontWeight.semiBold} color={COLORS.primary}>
              Verification Required
            </Text>
          </View>

          <Text
            fontSize={TYPOGRAPHY.fontSize.xl}
            fontWeight={TYPOGRAPHY.fontWeight.bold}
            color={COLORS.textPrimary}
            marginTop={SPACING.xs}
            marginBottom={SPACING.xs}
          >
            Become a Verified Landlord
          </Text>
          <Text fontSize={TYPOGRAPHY.fontSize.base} color={COLORS.textSecondary} lineHeight={22}>
            {isHome
              ? "Access tlyfePro flows by completing these checks. Head over to your Account tab to get started."
              : "Complete the checks below to unlock all Hubita features and access tlyfePro."}
          </Text>
        </View>

        {/* CTA button */}
        {!paymentDone ? (
          <TouchableOpacity
            style={[s.ctaBtn, isMobile && s.ctaBtnMobile]}
            onPress={handleVerifyPress}
            activeOpacity={0.85}
          >
            <MaterialCommunityIcons name="shield-check" size={16} color={COLORS.textWhite} style={{ marginRight: 6 }} />
            <Text fontSize={TYPOGRAPHY.fontSize.base} fontWeight={TYPOGRAPHY.fontWeight.semiBold} color={COLORS.textWhite}>
              {isHome ? 'Continue Setup' : 'Verify Yourself'}
            </Text>
          </TouchableOpacity>
        ) : (
          <View style={[s.ctaBtnDone, isMobile && s.ctaBtnMobile]}>
            <Feather name="check-circle" size={15} color={COLORS.success} style={{ marginRight: 6 }} />
            <Text fontSize={TYPOGRAPHY.fontSize.base} fontWeight={TYPOGRAPHY.fontWeight.semiBold} color={COLORS.success}>
              Payment Done
            </Text>
          </View>
        )}
      </View>

      {/* ── Progress bar ── */}
      <ProgressBar progress={progress} completedCount={completedCount} />

      {/* ── Divider ── */}
      <View style={s.divider} />

      {/* ── Verification Cards ── */}
      <View style={[s.cardsRow, isMobile && s.cardsRowMobile]}>
        <VerifCard
          icon={require('../../assets/images/idveriufation.png')}
          title="ID Verification"
          description="Upload a government-issued identity document to verify who you are."
          verified={idVerified}
          disabled={!paymentDone || idVerified}
          btnLabel={idVerified ? 'Verified' : !paymentDone ? 'Locked' : 'Start Verification →'}
          onPress={handleIdPress}
        />
        <VerifCard
          icon={require('../../assets/images/kyc.png')}
          title="Know Your Customer (KYC)"
          description="Complete your KYC check to unlock all Hubita platform features."
          verified={kycVerified}
          disabled={!paymentDone || kycVerified}
          btnLabel={kycVerified ? 'Verified' : !paymentDone ? 'Locked' : 'Start KYC →'}
          onPress={() => { if (paymentDone && !kycVerified) setShowComingSoonPopup(true); }}
        />
      </View>

      {/* ── Modals ── */}
      <VerifyModal
        visible={openModal}
        onClose={() => {
          setOpenModal(false);
          fetchUserProfile();
        }}
      />

      <PopUp
        modalVisible={showComingSoonPopup}
        titleText="Coming Soon"
        titleColor={COLORS.primary}
        subTitleText="KYC verification will be available soon. Stay tuned!"
        subTitleColor={COLORS.textPrimary}
        actionButtons={[{ text: 'Got it', width: 120, onPress: () => setShowComingSoonPopup(false) }]}
        closePopup={() => setShowComingSoonPopup(false)}
      />
    </View>
  );
};

export default VerificationSection;

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  container: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: SPACING.xl,
    marginVertical: SPACING.base,
    ...SHADOWS.card,
    overflow: 'hidden',
  },
  loadingOverlay: {
    position: 'absolute',
    top: SPACING.base,
    right: SPACING.base,
    zIndex: 10,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: SPACING.lg,
    gap: SPACING.base,
  },
  headerMobile: {
    flexDirection: 'column',
  },
  headerTextWrap: {
    flex: 1,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: COLORS.primaryLight,
    borderRadius: BORDER_RADIUS.full,
    paddingHorizontal: SPACING.sm,
    paddingVertical: 4,
    marginBottom: SPACING.xs,
  },

  // CTA buttons
  ctaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.primary,
    paddingVertical: SPACING.sm + 2,
    paddingHorizontal: SPACING.base,
    borderRadius: BORDER_RADIUS.full,
    alignSelf: 'flex-start',
    flexShrink: 0,
    ...SHADOWS.sm,
  },
  ctaBtnDone: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.successLight,
    paddingVertical: SPACING.sm + 2,
    paddingHorizontal: SPACING.base,
    borderRadius: BORDER_RADIUS.full,
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  ctaBtnMobile: {
    alignSelf: 'flex-start',
    marginTop: SPACING.sm,
  },

  // Progress
  progressWrapper: {
    marginBottom: SPACING.base,
  },
  progressLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: SPACING.xs,
  },
  progressTrack: {
    height: 6,
    backgroundColor: COLORS.borderLight,
    borderRadius: BORDER_RADIUS.full,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: COLORS.primary,
    borderRadius: BORDER_RADIUS.full,
  },

  divider: {
    height: 1,
    backgroundColor: COLORS.divider,
    marginBottom: SPACING.base,
  },

  // Cards row
  cardsRow: {
    flexDirection: 'row',
    gap: SPACING.base,
  },
  cardsRowMobile: {
    flexDirection: 'column',
  },

  // Individual card
  verifCard: {
    flex: 1,
    backgroundColor: COLORS.appBg,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.base,
  },
  verifCardDone: {
    borderColor: COLORS.successLight,
    backgroundColor: '#F0FDF8',
  },
  verifCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: SPACING.xs,
  },

  // Icon
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: BORDER_RADIUS.md,
    backgroundColor: COLORS.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapDone: {
    backgroundColor: COLORS.successLight,
  },
  cardIcon: {
    width: 28,
    height: 28,
  },

  // Badge
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.sm,
    paddingVertical: 3,
    borderRadius: BORDER_RADIUS.full,
  },
  badgeDone: {
    backgroundColor: COLORS.successLight,
  },
  badgePending: {
    backgroundColor: COLORS.warningLight,
  },

  // Card action button
  cardBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.base,
    borderRadius: BORDER_RADIUS.lg,
    marginTop: SPACING.base,
  },
  cardBtnActive: {
    backgroundColor: COLORS.primary,
    ...SHADOWS.sm,
  },
  cardBtnLocked: {
    backgroundColor: COLORS.borderLight,
  },
  cardBtnDone: {
    backgroundColor: COLORS.successLight,
  },
});
